import { ignoreHeaderNames } from "../../components/cardview/constants/CardViewConstants";
import { convertRowsToObjectWithIdAsKey } from "../helpersSubModule";
import { checkIfRollupEnabledInMDV } from "../xTablesHelper";
import { getParentDateRollupForFlatRows, getParentStatusRollupForFlatRows } from "./rowChangePropagation";

export interface Column {
  azureColumn: any;
  field: keyof Row;
  id?: string;
  headerName?: string;
  type?: string;
  rollup?: string;
  block?: string;
  children?: Column[];
  linkedColumns?: Record<string, string>;
  listData?: unknown;
  status?: string;
  // Add other props you use here
}
interface Row {
  id: string | number;
  data?: any;
  [key: string]: any;
}
export const getUniqueParentRowsFromLidUsingMap = (
  rowByIdMap,
  targetRows
) => {
  if (!rowByIdMap || !targetRows?.length) return [];

  const parentIds = new Set();

  for (const row of targetRows) {
    if (!row?.l_id) continue;

    const parts = row.l_id.split('.');
    const selfId = row.id;

    for (let i = 0; i < parts.length - 1; i++) {
      const id = parts[i];
      if (id !== selfId) parentIds.add(id);
    }
  }

  const parents: any[] = [];
  for (const id of parentIds) {
    const parentRow = rowByIdMap.get(id);
    if (parentRow) parents.push(parentRow);
  }

  return parents;
};
export interface RollupContext {
  predecessorColumn?: Column | null;
  durationColumn?: Column | null;

  predecessorStartDateColumn?: Column | null;
  predecessorEndDateColumn?: Column | null;

  dateRollupColumns: Column[];
  numberRollupColumns: Column[];
  mdvRollupColumns: Column[];
  shareableRollupListColumns: Column[];

  timeSeriesColumns: Column[];
  hasTimeSeriesInSheet: boolean;

  totalColumn?: Column | null;
}

/**
 * Derive all rollup-related columns and metadata from headerNameToColObjForCard
 * @param headerNameToColObjForCard - mapping from header name to Column object
 */
export const deriveRollupContextForCardView = (
  headerNameToColObjForCard: Record<string, Column>
): RollupContext => {
  const allColumns = Object.values(headerNameToColObjForCard || {}).filter(
    (col: any) => !ignoreHeaderNames.has(col.headerName) && col.block !== "timeseries"
  );

  /* ------------------ Predecessor column ------------------ */
  const predecessorColumn = allColumns.find(
    (col) => col.type === "predecessor"
  ) || null;
  const durationColumn = allColumns.find(
    (col) => col.type === "duration"
  ) || null;
  /* ------------------ Linked predecessor date columns ------------------ */
  const linkedCols = predecessorColumn?.linkedColumns || {};

  const predecessorStartDateColumn =
    linkedCols.predecessorStartDate && headerNameToColObjForCard[linkedCols.predecessorStartDate]
      ? headerNameToColObjForCard[linkedCols.predecessorStartDate]
      : null;

  const predecessorEndDateColumn =
    linkedCols.predecessorEndDate && headerNameToColObjForCard[linkedCols.predecessorEndDate]
      ? headerNameToColObjForCard[linkedCols.predecessorEndDate]
      : null;

  /* ------------------ Date rollup columns (Min / Max) ------------------ */
  const dateRollupColumns = allColumns.filter(
    (col) =>
      col.type === "date" &&
      (col.rollup === "Min" || col.rollup === "Max")
  );

  /* ------------------ Number rollup columns ------------------ */
  const numberRollupColumns = allColumns.filter(
    (col) =>
      col.type === "number" &&
      ["Sum", "Avg", "Min", "Max"].includes(col.rollup || "")
  );

  /* ------------------ MDV rollup-enabled columns ------------------ */
  const mdvRollupColumns = allColumns.filter(
    (col) =>
      col.type === "multi dimensional value" &&
      checkIfRollupEnabledInMDV(col?.listData)
  );

  /* ------------------ Shareable rollup list columns ------------------ */
  const shareableRollupListColumns = allColumns.filter(
    (col) => col.type === "shareableRollupList"
  );

  /* ------------------ TimeSeries columns ------------------ */
  // TimeSeries columns are excluded from allColumns above, so now get them separately from all columns ignoring filters
  const timeSeriesColumns = Object.values(headerNameToColObjForCard || {}).filter(
    (col) => col.block === "timeseries" && !col.children
  );

  const hasTimeSeriesInSheet = timeSeriesColumns.length > 0;

  /* ------------------ Total column ------------------ */
  const totalColumn =
    allColumns.find((col) => col.headerName === "Total" && col.block) || null;

  /* ------------------ Return context ------------------ */
  return {
    predecessorColumn,
    durationColumn,
    predecessorStartDateColumn,
    predecessorEndDateColumn,
    dateRollupColumns,
    numberRollupColumns,
    mdvRollupColumns,
    shareableRollupListColumns,
    timeSeriesColumns,
    hasTimeSeriesInSheet,
    totalColumn,
  };
};

interface RowWithId {
  id: string;
  data?: any;
  // add other properties you expect
}
interface ProcessParentNodesParams {
  sortedParentNodes: any;
  changedRowsObjWithId: any;
  additionalRowsForUndo: Record<string | number, Row>;
  dateRollupColumns: Column[];
  getRowNodeById: (id: string | number) => Node | null | undefined;
  predecessorStartDate: string | undefined;
  predecessorEndDate: string | undefined;
  predecessor: string | undefined;
  duration: string | undefined;
  endDate: string | undefined;
  startDate: string | undefined;
  endDateColumn: Column;
  totalColumn: Column | null;
  timeSeriesColumn: Column | null;
  sortedParentNodesAll: any;
  loggedInUser: any | null;
  rows: any
  rowByIdMap: any
}
interface ProcessDateRollupsParams {
  parentNode: Node;
  parentNodeData: Row;
  dateRollupColumns: Column[];
  updatedChildRowData: Record<string | number, Row>;
  getRowNodeById: (id: string | number) => Node | null | undefined;
  predecessorStartDate?: string;
  predecessorEndDate?: string;
  predecessor?: string;
  duration?: string;
  endDate?: string;
  startDate?: string;
  endDateColumn: Column;
  endDateBeforeProcessing: any;
  additionalRowsForUndo: Record<string | number, Row>;
  totalColumn: Column | null;
  timeSeriesColumn: Column | null;
  sortedParentNodes: Node[];
  loggedInUser: any | null;
  getAllParentsOfANode: (node: Node) => Node[];
  rows: any
  rowByIdMap: any
}

export function processDateRollupsAndSuccessorNodesForCardView({
  parentNode,
  parentNodeData,
  dateRollupColumns,
  updatedChildRowData,
  getRowNodeById,
  predecessorStartDate,
  predecessorEndDate,
  predecessor,
  duration,
  endDate,
  startDate,
  endDateColumn,
  endDateBeforeProcessing,
  additionalRowsForUndo,
  totalColumn,
  timeSeriesColumn,
  sortedParentNodes,
  loggedInUser,
  getAllParentsOfANode,
  rows,
  rowByIdMap
}: ProcessDateRollupsParams) {
  let columnTypeObj: Record<string, Column> = {};
  dateRollupColumns.forEach((colDef: any) => {
    if (colDef.type === 'shareableRollupList' && !colDef.azureColumn) {
      getParentStatusRollupForFlatRows(
        parentNode,          // the parent row object to update         
        colDef,       // column definition object for the status column
        updatedChildRowData,// object to track updated rows
        loggedInUser,       // user info, can be null if not needed
        rowByIdMap,
        rows        // (optional) name of the parent ID field in child rows
      );

    }
    else {
      getParentDateRollupForFlatRows({
        parentRow: parentNode,
        column: colDef,
        rollupType: colDef.rollup,
        updatedChildRowData,
        rows,
        loggedInUser,
      });
    }

  });


}


export function processParentNodesForCardView({
  sortedParentNodes,
  changedRowsObjWithId,
  additionalRowsForUndo,
  dateRollupColumns,
  getRowNodeById,
  predecessorStartDate,
  predecessorEndDate,
  predecessor,
  duration,
  endDate,
  startDate,
  endDateColumn,
  totalColumn,
  timeSeriesColumn,
  sortedParentNodesAll,
  loggedInUser,
  rows,
  rowByIdMap
}: ProcessParentNodesParams) {
  sortedParentNodes.forEach((parentNode: any) => {
    let undoRow: Row;
    let parentNodeData: Row;
    let updatedParentNodeData: Node | Row | null | undefined;

    if (!changedRowsObjWithId[parentNode.id]) {
      updatedParentNodeData = getRowNodeById(parentNode.id) ?? parentNode;
      parentNodeData = updatedParentNodeData && 'data' in updatedParentNodeData && updatedParentNodeData.data
        ? updatedParentNodeData.data
        : (parentNode as Row).data;

    } else {
      parentNodeData = changedRowsObjWithId[parentNode.id];

    }

    const endDateBeforeProcessing = ''

    processDateRollupsAndSuccessorNodesForCardView({
      parentNode,
      parentNodeData,
      dateRollupColumns,
      updatedChildRowData: changedRowsObjWithId,
      getRowNodeById,
      predecessorStartDate,
      predecessorEndDate,
      predecessor,
      duration,
      endDate,
      startDate,
      endDateColumn,
      endDateBeforeProcessing,
      additionalRowsForUndo,
      totalColumn,
      timeSeriesColumn,
      sortedParentNodes: sortedParentNodesAll,
      loggedInUser,
      getAllParentsOfANode: (node) => {
        // You must provide this function from your environment
        throw new Error('getAllParentsOfANode function must be implemented and injected');
      },
      rows,
      rowByIdMap
    });

  });
}

export function getParentRowsAfterRollupForCardView(
  updatedRows,
  parentNodes,
  rollupContext,
  getRowByIdMap,
  loggedInUser,
  rowByIdMap,
  rows
) {

  const {
    predecessorColumn,
    durationColumn,
    predecessorStartDateColumn,
    predecessorEndDateColumn,
    dateRollupColumns,
    numberRollupColumns,
    mdvRollupColumns,
    shareableRollupListColumns,
    timeSeriesColumns,
    hasTimeSeriesInSheet,
    totalColumn,
  } = rollupContext;
  let allRollupColumns = [
    ...dateRollupColumns,
    ...numberRollupColumns,
    ...mdvRollupColumns,
    ...shareableRollupListColumns,
    ...(hasTimeSeriesInSheet ? timeSeriesColumns : []),
  ];
  // Defensive fallback for linked columns in predecessor
  const {
    predecessorStartDate,
    predecessorEndDate,
    predecessor,
    duration,
    endDate,
    startDate,
  } = predecessorColumn?.linkedColumns || {};



  let additionalRowsForUndo: Record<string, Row> = {};
  let changedRowsObjWithId = convertRowsToObjectWithIdAsKey(updatedRows);
  let initialRowObj: Record<string, RowWithId> = { ...changedRowsObjWithId };


  if (parentNodes.size > 0) {

    const sortedParentNodes = Array.from(parentNodes).sort((a: any, b: any) => {
      const indexA = rows.findIndex(row => row.id === a.id);
      const indexB = rows.findIndex(row => row.id === b.id);
      return indexB - indexA;
    });

    processParentNodesForCardView({
      sortedParentNodes,
      changedRowsObjWithId,
      additionalRowsForUndo,
      dateRollupColumns: allRollupColumns,
      getRowNodeById: getRowByIdMap,
      predecessorStartDate,
      predecessorEndDate,
      predecessor: predecessor || predecessorColumn.field,
      duration: duration || durationColumn.field,
      endDate: endDate || predecessorColumn?.linkedColumns?.predecessorEndDate,
      startDate: startDate || predecessorColumn?.linkedColumns?.predecessorStartDate,
      endDateColumn: predecessorEndDateColumn!,
      totalColumn,
      timeSeriesColumn: timeSeriesColumns?.[0] || null,
      sortedParentNodesAll: sortedParentNodes,
      loggedInUser,
      rows,
      rowByIdMap
    });

  }

  // Prepare parent rows affected by rollup for return
  let parentRowsAffectedByRollup: Row[] = [];
  Object.keys(changedRowsObjWithId).forEach((changedRow) => {
    if (!initialRowObj[changedRowsObjWithId[changedRow].id]) {
      if (changedRowsObjWithId[changedRow].data) {
        parentRowsAffectedByRollup.push(changedRowsObjWithId[changedRow].data);
      } else {
        parentRowsAffectedByRollup.push(changedRowsObjWithId[changedRow]);
      }
    }
  });

  let additionalParentAndExtraRowsForUndo: Row[] = [];
  Object.keys(additionalRowsForUndo).forEach((row) => {
    additionalParentAndExtraRowsForUndo.push(additionalRowsForUndo[row]);
  });

  // Merge rows (handle priority between merged rows)
  const mergedRowsAfterRollupAndPredecessorChange = []

  return {
    parentRowsAffectedByRollup,
    additionalParentAndExtraRowsForUndo,
    mergedRowsAfterRollupAndPredecessorChange,
  };
};